'use client';

import { Tooltip, TooltipContent, TooltipTrigger } from '@workspace/ui/components/tooltip';
import { cn } from '@workspace/ui/lib';
import { InfoIcon } from 'lucide-react';
import * as React from 'react';

type InfoTooltipPositionProps = {
  side?: React.ComponentProps<typeof TooltipContent>['side'];
  align?: React.ComponentProps<typeof TooltipContent>['align'];
  sideOffset?: number;
};

type InfoTooltipContextValue = InfoTooltipPositionProps & {
  accessibleLabel?: string;
};

const InfoTooltipContext = React.createContext<InfoTooltipContextValue | null>(null);

type ReactElementType = React.ReactElement['type'];

function useInfoTooltipContext(component: string) {
  const context = React.useContext(InfoTooltipContext);
  if (!context) {
    throw new Error(`${component} must be used within InfoTooltip`);
  }
  return context;
}

function getPartDisplayName(type: ReactElementType): string | undefined {
  if (typeof type === 'function' || typeof type === 'object') {
    return (type as { displayName?: string }).displayName;
  }
  return undefined;
}

function isInfoTooltipTriggerType(type: ReactElementType): boolean {
  return (
    type === InfoTooltipTrigger ||
    type === InfoTooltip.Trigger ||
    getPartDisplayName(type) === 'InfoTooltipTrigger'
  );
}

function isInfoTooltipContentType(type: ReactElementType): boolean {
  return (
    type === InfoTooltipContent ||
    type === InfoTooltip.Content ||
    getPartDisplayName(type) === 'InfoTooltipContent'
  );
}

function getContentChild(children: React.ReactNode): React.ReactElement | undefined {
  let contentChild: React.ReactElement | undefined;

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (isInfoTooltipContentType(child.type)) {
      contentChild = child;
    }
  });

  return contentChild;
}

function hasInfoTooltipTrigger(children: React.ReactNode): boolean {
  let found = false;

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (isInfoTooltipTriggerType(child.type)) {
      found = true;
    }
  });

  return found;
}

/** Avoid duplicating the icon when an explicit trigger is passed from a Server Component. */
function shouldRenderDefaultTrigger(children: React.ReactNode): boolean {
  if (hasInfoTooltipTrigger(children)) return false;

  const elements = React.Children.toArray(children).filter(React.isValidElement);
  if (elements.length === 0) return true;
  // One child is the common content-only pattern; two+ implies trigger + content.
  if (elements.length === 1) return true;

  return elements.every((child) => isInfoTooltipContentType(child.type));
}

function resolveAccessibleLabel(children: React.ReactNode, ariaLabel?: string): string | undefined {
  if (ariaLabel) return ariaLabel;

  const contentChild = getContentChild(children);
  const content = contentChild
    ? (contentChild.props as { children?: React.ReactNode }).children
    : undefined;

  return typeof content === 'string' ? content : undefined;
}

export type InfoTooltipProps = InfoTooltipPositionProps & {
  children: React.ReactNode;
  'aria-label'?: string;
};

/**
 * Standard info ("i") tooltip. Compose with `InfoTooltipContent` (required) and
 * `InfoTooltipTrigger` (optional). In Server Components, import subcomponents by name
 * instead of `InfoTooltip.Trigger` / `InfoTooltip.Content`.
 */
function InfoTooltip({
  children,
  side,
  align,
  sideOffset,
  'aria-label': ariaLabel,
}: InfoTooltipProps) {
  const accessibleLabel = resolveAccessibleLabel(children, ariaLabel);

  return (
    <InfoTooltipContext.Provider value={{ side, align, sideOffset, accessibleLabel }}>
      <Tooltip>
        {shouldRenderDefaultTrigger(children) && <InfoTooltipTrigger />}
        {children}
      </Tooltip>
    </InfoTooltipContext.Provider>
  );
}

type InfoTooltipTriggerProps = {
  className?: string;
  'aria-label'?: string;
};

function InfoTooltipTrigger({ className, 'aria-label': ariaLabel }: InfoTooltipTriggerProps) {
  const { accessibleLabel } = useInfoTooltipContext('InfoTooltipTrigger');

  return (
    <TooltipTrigger asChild>
      <span
        role="button"
        aria-label={ariaLabel ?? accessibleLabel}
        tabIndex={0}
        className={cn('inline-flex shrink-0 cursor-default items-center text-current', className)}
      >
        <InfoIcon className="size-[1em] stroke-2" aria-hidden />
      </span>
    </TooltipTrigger>
  );
}

type InfoTooltipContentProps = {
  className?: string;
  children: React.ReactNode;
};

function InfoTooltipContent({ className, children }: InfoTooltipContentProps) {
  const { side, align, sideOffset } = useInfoTooltipContext('InfoTooltipContent');

  return (
    <TooltipContent side={side} align={align} sideOffset={sideOffset} className={className}>
      {typeof children === 'string' ? <p className="text-inherit">{children}</p> : children}
    </TooltipContent>
  );
}

InfoTooltipTrigger.displayName = 'InfoTooltipTrigger';
InfoTooltipContent.displayName = 'InfoTooltipContent';

InfoTooltip.Trigger = InfoTooltipTrigger;
InfoTooltip.Content = InfoTooltipContent;

export { InfoTooltip, InfoTooltipTrigger, InfoTooltipContent };
